import React, { useEffect, useRef } from "react";
import {
	type ClearIndicatorProps,
	components,
	type ContainerProps,
	type DropdownIndicatorProps,
	type GroupBase,
	type MenuProps,
	type OptionProps,
	type SingleValueProps,
	type StylesConfig,
} from "react-select";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp, faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Tooltip } from "~/ui";

import styles from "./Selector.module.css";

export interface SelectorOption {
	readonly label: string;
	readonly value: string;
	readonly image?: string;
	readonly icon?: IconDefinition;
	readonly isDisabled?: boolean;
	readonly "data-test-id"?: string;
	readonly hoverTooltipWhenDisabled?: string;
}

declare module "react-select/dist/declarations/src/Select" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
		"data-test-id"?: string;
	}
}

export const SelectContainer = <T extends {}>({
	children,
	innerProps,
	selectProps,
	...rest
}: ContainerProps<T, false>) => {
	// @ts-ignore - Adding 'data-test-id' to the global HTML attribute would fix this but this works for now
	innerProps["data-test-id"] = selectProps["data-test-id"];

	return (
		<components.SelectContainer {...rest} innerProps={innerProps} selectProps={selectProps}>
			{children}
		</components.SelectContainer>
	);
};

export const DropdownIndicator = <T extends {}>({ children: _children, ...rest }: DropdownIndicatorProps<T, false>) => {
	return (
		<components.DropdownIndicator {...rest}>
			<FontAwesomeIcon icon={rest.selectProps.menuIsOpen ? faCaretUp : faCaretDown} />
		</components.DropdownIndicator>
	);
};

export const ClearIndicator = <T extends {}>({ children: _children, ...rest }: ClearIndicatorProps<T, false>) => {
	return (
		<components.ClearIndicator {...rest}>
			<FontAwesomeIcon icon={faTimes} />
		</components.ClearIndicator>
	);
};

export const IndicatorSeparator = () => null;

export const Option = <
	T extends {
		"data-test-id"?: string;
		image?: string;
		icon?: IconDefinition;
		isDisabled?: boolean;
		hoverTooltipWhenDisabled?: string;
	},
>({
	children,
	data,
	innerProps,
	...rest
}: OptionProps<T, false>) => {
	const { isSelected, isDisabled } = rest;
	const textRef = useRef<HTMLSpanElement | null>(null);

	// @ts-ignore - Adding 'data-test-id' to the global HTML attribute would fix this but this works for now
	innerProps["data-test-id"] = data["data-test-id"];
	const image = data["image"];
	const icon = data["icon"];

	useEffect(() => {
		if (textRef.current !== null && isSelected) {
			textRef.current.scrollIntoView?.();
		}
	}, [textRef, isSelected]);

	return (
		<Tooltip
			content={data["hoverTooltipWhenDisabled"]}
			maxWidth={200}
			placement="right"
			disabled={!isDisabled || !data["hoverTooltipWhenDisabled"]}
			className={styles.tooltipContainer}
		>
			<span>
				<components.Option {...rest} data={data} innerProps={innerProps}>
					{icon && (
						<span className={styles.optionVisual}>
							<FontAwesomeIcon icon={icon} className={styles.optionImage} />
						</span>
					)}
					{image && (
						<span className={styles.optionVisual}>
							<img src={image} className={styles.optionImage} />
						</span>
					)}
					<span ref={textRef} className={styles.optionText}>
						{children}
					</span>
					{isSelected && <FontAwesomeIcon icon={faCheck} className={styles.optionChecked} />}
				</components.Option>
			</span>
		</Tooltip>
	);
};

export const SingleValue = <T extends { icon?: IconDefinition; image?: string }>({
	children,
	...rest
}: SingleValueProps<T, false>) => {
	const { getValue } = rest;
	const value = getValue()[0];

	const icon = value["icon"];
	const image = value["image"];

	return (
		<components.SingleValue {...rest}>
			{icon && (
				<>
					<FontAwesomeIcon icon={icon} className={styles.optionImage} />
					&ensp;
				</>
			)}
			{image && (
				<span className={styles.optionVisual}>
					<img src={image} className={styles.optionImage} />
				</span>
			)}
			{children}
		</components.SingleValue>
	);
};

export const MenuWithForcedDarkTheme = <T extends {}>({ children, ...rest }: MenuProps<T, false>) => {
	return (
		<components.Menu {...rest} className="force-dark-theme">
			{children}
		</components.Menu>
	);
};

const similarStyles: StylesConfig = {
	input: base => ({ ...base, color: "var(--primaryTextColor)", margin: 0, padding: 0 }),
	menu: base => ({
		...base,
		backgroundColor: "var(--flyoutBackgroundColor)",
		borderRadius: "0.375rem",
		boxShadow: "var(--flyoutBoxShadow)",
		color: "var(--primaryTextColor)",
		fontSize: "14px",
		lineHeight: 1.14,
		margin: "0.125rem 0",
		minWidth: "fit-content",
	}),
	menuList: base => ({ ...base, padding: "0.25rem" }),
	group: () => ({
		color: "var(--secondaryTextColor)",
		"&:not(:first-child)": { borderTop: "1px solid var(--dividerColor)", marginTop: "0.5rem", paddingTop: "0.5rem" },
	}),
	groupHeading: () => ({
		padding: "0.5rem",
		fontSize: "0.75rem",
	}),
};

export const condensedStyles: StylesConfig = {
	...similarStyles,
	control: (base, state) => ({
		...base,
		backgroundColor: `var(--${
			state.selectProps.menuIsOpen ? "veryMutedRoyal" : state.isFocused ? "veryMutedGrey" : "baseLayerColor"
		})`,
		border: "none",
		boxShadow: "none",
		borderRadus: "0.25rem",
		font: "var(--paragraph-4-emphasis)",
		minHeight: "unset",
		color: `var(--${
			state.isDisabled ? "placeholderTextColor" : state.selectProps.menuIsOpen ? "vividRoyal" : "primaryTextColor"
		})`,
		":hover": { "@media (hover: hover)": { backgroundColor: "var(--veryMutedGrey)", cursor: "pointer" } },
	}),
	dropdownIndicator: base => ({
		...base,
		color: "inherit",
		padding: "0.25rem 0.5rem 0.25rem 0.25rem",
		":hover": { color: "inherit" },
	}),
	clearIndicator: base => ({
		...base,
		color: "var(--primaryTextColor)",
		opacity: 0.3,
		padding: "0.25rem 0.5rem 0.25rem 0.25rem",
		":hover": { color: "var(--primaryTextColor)", opacity: 1 },
	}),
	placeholder: (base, state) => ({
		...base,
		color: `var(--${state.isDisabled ? "placeholderTextColor" : "primaryTextColor"})`,
		margin: 0,
	}),
	singleValue: base => ({
		...base,
		color: "inherit",
	}),
	valueContainer: base => ({
		...base,
		padding: "0.25rem 0.25rem 0.25rem 0.5rem",
	}),
	option: (base, state) => ({
		...base,
		alignItems: "center",
		backgroundColor: state.isFocused ? "var(--baseLayerColor)" : "var(--flyoutBackgroundColor)",
		borderRadius: "6px",
		color: `var(--${state.isDisabled ? "placeholderTextColor" : state.isSelected ? "linkColor" : "primaryTextColor"})`,
		display: "flex",
		padding: "0.375rem 0.75rem",
		whiteSpace: "nowrap",
		":hover": { cursor: state.isDisabled ? "not-allowed" : "pointer" },
		// The below style is specific to the Chrome browser which has a blue background when an option is selected without it
		":active": { backgroundColor: "var(--baseLayerColor)" },
	}),
};

export const outlinedStyles: StylesConfig = {
	...similarStyles,
	control: (base, state) => ({
		...base,
		backgroundColor: `var(--${state.isDisabled ? "veryMutedGrey" : "secondLayerColor"})`,
		border: `1px solid var(--${state.isFocused ? "vividRoyal" : "inputBorderColor"})`,
		borderRadius: "6px",
		boxShadow: state.isFocused ? "0px 0px 0px 2px rgba(var(--vividRoyal-rgb), 0.1)" : "none",
		fontSize: "14px",
		lineHeight: 1.14,
		minHeight: "unset",
		"> :last-child": {
			backgroundColor: `var(--${
				state.isFocused ? "veryMutedRoyal" : state.isDisabled ? "veryMutedGrey" : "secondLayerColor"
			})`,
			borderRadius: "6px",
		},
		":hover": { border: `1px solid var(--${state.isFocused ? "vividRoyal" : "inputBorderColor"})`, cursor: "pointer" },
		":hover > :last-child": { backgroundColor: `var(--${state.isFocused ? "veryMutedRoyal" : "veryMutedGrey"})` },
	}),
	dropdownIndicator: (base, state) => ({
		...base,
		color: `var(--${
			state.isFocused ? "primaryButtonColor" : state.isDisabled ? "placeholderTextColor" : "primaryTextColor"
		})`,
		padding: "0.438rem 0.625rem",
		":hover": { color: `var(--${state.isFocused ? "primaryButtonColor" : "primaryTextColor"})` },
	}),
	clearIndicator: (base, state) => ({
		...base,
		opacity: 0.3,
		color: `var(--${state.isFocused ? "primaryButtonColor" : "primaryTextColor"})`,
		padding: "0.438rem 0.625rem",
		":hover": { color: `var(--${state.isFocused ? "primaryButtonColor" : "primaryTextColor"})`, opacity: 1 },
	}),
	indicatorsContainer: base => ({ ...base, fontSize: "16px" }),
	placeholder: base => ({ ...base, color: "var(--placeholderTextColor)", margin: 0, padding: 0 }),
	singleValue: base => ({ ...base, color: "var(--primaryTextColor)", display: "flex" }),
	valueContainer: base => ({
		...base,
		// Minus one pixel to account for space the border takes up
		padding: "calc(0.5rem - 1px)",
		paddingLeft: "calc(0.75rem - 1px)",
		/* Semantic being Semantic is enforcing its padding styling over our own on a dummy input
		and we need to up the specificity to maintain the appearance of the Selector.
		When we remove Semantic, we can remove this too.
		https://github.com/JedWatson/react-select/issues/3995
		*/
		input: { padding: "0 !important" },
	}),
	option: (base, state) => ({
		...base,
		alignItems: "center",
		backgroundColor: state.isFocused ? "var(--baseLayerColor)" : "var(--flyoutBackgroundColor)",
		borderRadius: "6px",
		color: `var(--${state.isDisabled ? "placeholderTextColor" : state.isSelected ? "linkColor" : "primaryTextColor"})`,
		display: "flex",
		padding: "0.375rem 0.75rem",
		":hover": { cursor: state.isDisabled ? "not-allowed" : "pointer" },
		// The below style is specific to the Chrome browser which has a blue background when an option is selected without it
		":active": { backgroundColor: "var(--baseLayerColor)" },
	}),
};
